import { AddIcon, BackIcon, BugIcon, CloseIcon, MailIcon, StopIcon } from '@assets/icons';
import { Button } from '@components/button';
import { IconButton } from '@components/button/IconButton';
import { TextInput } from '@components/form';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { devConsole } from '@helper-functions/devConsole';
import { agentPageContextAtom } from '@hooks/agentPanelAtoms';
import { Box, Paper, Popper, Stack, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import posthog from 'posthog-js';
import { useEffect, useRef, useState } from 'react';
import { AgentChatMessage } from './AgentChatMessage';
import { useAgentStream } from './useAgentStream';

/** Slash commands available in the chat input */
const SLASH_COMMANDS = [
  {
    command: '/bug',
    label: 'Report a bug',
    description: 'Submit a bug report to the team',
    icon: BugIcon,
  },
  {
    command: '/feedback',
    label: 'Give feedback',
    description: 'Share a suggestion or feedback',
    icon: MailIcon,
  },
] as const;

type AgentChatViewProps = {
  token: string;
  conversationId: string;
  conversationTitle?: string;
  /** Called when user clicks the back arrow to return to conversation list */
  onBack: () => void;
  /** Called when user clicks "New Chat" to start a fresh conversation */
  onNewChat: () => void;
};

/**
 * Active chat view — shows message history, streams agent responses,
 * and provides a text input for sending messages.
 */
export const AgentChatView = ({
  token,
  conversationId,
  conversationTitle,
  onBack,
  onNewChat,
}: AgentChatViewProps) => {
  const {
    messages,
    streaming,
    isRecovering,
    queueCount,
    sendMessage: send,
    stopStreaming,
    respondToRichBlock,
    loadHistory,
    clearMessages,
  } = useAgentStream(token, conversationId, {
    // Silent recovery — the auto-retry handles reconnection seamlessly
    onSessionRecovered: () => {
      devConsole.info('[AgentChatView] Session recovered from backend fallback');
    },
  });
  const { palette } = useTheme();
  const pageContext = useAtomValue(agentPageContextAtom);

  const [input, setInput] = useState('');
  const [pastedImages, setPastedImages] = useState<string[]>([]);
  // Local display title — starts from prop, auto-sets from first message
  const [displayTitle, setDisplayTitle] = useState(conversationTitle);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const inputElementRef = useRef<HTMLInputElement>(null);

  // Slash command autocomplete state
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [slashMenuIndex, setSlashMenuIndex] = useState(0);

  // Filter commands based on what the user has typed after "/"
  const matchingCommands = input.startsWith('/')
    ? SLASH_COMMANDS.filter((cmd) => cmd.command.startsWith(input.split(' ')[0].toLowerCase()))
    : [];

  // Show the menu when the user is typing a slash command prefix and there are matches
  // Allowed during streaming so users can queue slash commands too
  const showSlashMenu = slashMenuOpen && matchingCommands.length > 0;

  // Sync display title when switching conversations
  useEffect(() => {
    setDisplayTitle(conversationTitle);
  }, [conversationId, conversationTitle]);

  // Load message history when conversation changes and auto-focus the input
  useEffect(() => {
    clearMessages();
    if (conversationId) {
      void loadHistory(conversationId);
    }
    // Auto-focus the text field so the user can start typing immediately
    setTimeout(() => {
      const el = inputElementRef.current; // ?.querySelector('input, textarea');
      el?.focus();
    }, 100);
  }, [conversationId, loadHistory, clearMessages]);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /** Send message handler — includes any pasted images.
   *  The hook's send() handles queuing if the agent is currently streaming. */
  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    const images = pastedImages.length > 0 ? [...pastedImages] : undefined;
    // Track message sent event
    const isSlashCommand = trimmed.startsWith('/');
    const command = isSlashCommand ? trimmed.split(' ')[0] : undefined;
    posthog.capture('agent-chat:user_message_sent', {
      organizationId: pageContext.organizationId,
      workspaceId: pageContext.workspaceId,
      conversationId,
      hasImages: (images?.length ?? 0) > 0,
      isSlashCommand,
      command,
    });
    setInput('');
    setPastedImages([]);
    // Auto-set header title from first message if not already set
    if (!displayTitle) {
      setDisplayTitle(trimmed.length > 80 ? `${trimmed.slice(0, 77)}...` : trimmed);
    }
    await send(trimmed, images);
  };

  /** Handle paste events — extract images from clipboard */
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    // for (const item of Array.from(items)) {
    Array.from(items).forEach((item) => {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) return;

        // Convert to base64 data URI
        const reader = new FileReader();
        reader.onload = () => {
          const dataUri = reader.result as string;
          // Cap at 5 images per message
          setPastedImages((prev) => (prev.length >= 5 ? prev : [...prev, dataUri]));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  /** Remove a pasted image by index */
  const removePastedImage = (index: number) => {
    setPastedImages((prev) => prev.filter((_, i) => i !== index));
  };

  /** Handle input changes — open slash menu when "/" is typed at the start */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInput(value);

    if (value.startsWith('/') && !value.includes(' ')) {
      // User is typing a command prefix — show the menu
      setSlashMenuOpen(true);
      setSlashMenuIndex(0);
    } else {
      setSlashMenuOpen(false);
    }
  };

  /** Select a slash command from the autocomplete menu */
  const selectSlashCommand = (cmd: string) => {
    posthog.capture('agent-chat:user_slash-command_used', {
      organizationId: pageContext.organizationId,
      workspaceId: pageContext.workspaceId,
      command: cmd,
    });
    // Insert the full command followed by a space so the user can
    // optionally add a description inline (e.g. "/bug the page crashed")
    setInput(`${cmd} `);
    setSlashMenuOpen(false);
  };

  /** Enter to send (or select), Shift+Enter for newline, arrows for menu */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSlashMenu) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashMenuIndex((i) => Math.min(i + 1, matchingCommands.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashMenuIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        selectSlashCommand(matchingCommands[slashMenuIndex].command);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlashMenuOpen(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      {/* Header bar with back button and title */}
      <Group
        alignItems="center"
        gap={1}
        sx={{
          px: 2,
          py: 2,
          bgcolor: 'header.secondary',
          minHeight: 48,
          borderBottom: '1px solid',
          borderColor: 'background.default',
          color: 'grey.300',
        }}
      >
        <IconButton
          Icon={BackIcon}
          onClick={onBack}
          color={palette.grey[300]}
          data-testid="agent-chat-back-btn"
        />
        <Typography variant="body2" color="grey.300" fontWeight={600} noWrap sx={{ flex: 1 }}>
          {displayTitle ?? 'New conversation'}
        </Typography>
        <IconButton
          Icon={AddIcon}
          onClick={onNewChat}
          color={palette.grey[300]}
          aria-label="New chat"
          data-testid="agent-chat-new-btn-inline"
        />
      </Group>

      {/* Messages area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          bgcolor: 'background.paperCard',
        }}
        data-testid="agent-chat-messages"
      >
        {messages.length === 0 && (
          <Typography variant="body2" color="foreground.paperCard.light" textAlign="center" mt={4}>
            Send a message to start the conversation.
          </Typography>
        )}
        {messages.map((msg, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <AgentChatMessage key={idx} message={msg} onRespondToBlock={respondToRichBlock} />
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <Stack gap={1} sx={{ p: 2, bgcolor: 'background.paper', position: 'relative' }}>
        {/* Pasted image thumbnails — shown above the input when images are queued */}
        {pastedImages.length > 0 && (
          <Group sx={{ gap: 2, px: 2, flexWrap: 'wrap' }} data-testid="pasted-images-preview">
            {pastedImages.map((dataUri, idx) => (
              <Box
                key={dataUri}
                sx={{
                  position: 'relative',
                  width: 64,
                  height: 64,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0,
                  '&:hover': {
                    borderColor: 'foreground.default.default',
                  },
                }}
              >
                <img
                  src={dataUri}
                  alt={`Attachment ${idx + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <IconButton
                  Icon={CloseIcon}
                  onClick={() => removePastedImage(idx)}
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 0.25,
                    '& .deckard-icon': { stroke: palette.foreground.default.default },
                    '&:hover': {
                      bgcolor: 'error.main',
                      '& .deckard-icon': { stroke: palette.error.contrastText },
                    },
                  }}
                  data-testid={`remove-pasted-image-${idx}`}
                />
              </Box>
            ))}
          </Group>
        )}
        {/* Reconnecting indicator — shows briefly during auto-recovery */}
        {isRecovering && (
          <Group
            sx={{
              alignItems: 'center',
              gap: 1,
            }}
            data-testid="agent-chat-reconnecting"
          >
            <Typography variant="caption2" color="warning.main">
              Reconnecting...
            </Typography>
          </Group>
        )}
        {/* Queue badge — shows how many messages are waiting while agent is busy */}
        {queueCount > 0 && !isRecovering && (
          <Group
            sx={{
              alignItems: 'center',
              gap: 1,
            }}
            data-testid="agent-chat-queue-badge"
          >
            <MailIcon size={14} />
            <Typography variant="caption2" color="foreground.paper.light">
              {queueCount === 1 ? '1 message queued' : `${queueCount} messages queued`}
            </Typography>
          </Group>
        )}
        <Group gap={1} alignItems="center" sx={{ position: 'relative' }}>
          {/* Slash command autocomplete menu — anchored above the input */}
          <Popper
            open={showSlashMenu}
            anchorEl={inputRef.current}
            placement="top-start"
            sx={{ zIndex: 1300, width: inputRef.current?.clientWidth || 'auto' }}
            data-testid="slash-command-menu"
          >
            <Paper
              elevation={8}
              sx={(theme) => ({
                mb: 0.5,
                border: `1px solid ${theme.palette.menu.border}`,
                bgcolor: 'menu.background',
                overflow: 'hidden',
                borderRadius: 1,
              })}
            >
              <Stack gap={0.5} p={0.5}>
                {matchingCommands.map((cmd, idx) => {
                  const Icon = cmd.icon;
                  return (
                    <Group
                      key={cmd.command}
                      alignItems="center"
                      gap={3}
                      onClick={() => selectSlashCommand(cmd.command)}
                      className={idx === slashMenuIndex ? 'selected' : ''}
                      sx={{
                        py: 1.5,
                        px: 3,
                        color: palette.foreground.default.light,
                        cursor: 'pointer',
                        '&.selected': {
                          bgcolor: 'menu.item.selected',
                          color: palette.foreground.paper.default,
                          '& .deckard-icon': {
                            color: palette.foreground.paper.default,
                          },
                          '&:hover': { bgcolor: 'menu.item.selected' },
                        },
                        '&:hover': {
                          bgcolor: 'menu.item.hover',
                          color: palette.foreground.paper.default,
                          '& .deckard-icon': {
                            color: palette.foreground.paper.default,
                          },
                        },
                      }}
                    >
                      <Icon size={18} />
                      <Stack gap={0.25}>
                        <Typography variant="body2" fontWeight={600}>
                          {cmd.command}
                        </Typography>
                        <Typography variant="caption2">{cmd.label}</Typography>
                      </Stack>
                    </Group>
                  );
                })}
              </Stack>
              {/* <List dense disablePadding>
                {matchingCommands.map((cmd, idx) => {
                  const Icon = cmd.icon;
                  return (
                    <ListItemButton
                      key={cmd.command}
                      selected={idx === slashMenuIndex}
                      onClick={() => selectSlashCommand(cmd.command)}
                      data-testid={`slash-cmd-${cmd.command.slice(1)}`}
                      sx={(theme) => ({
                        py: 1,
                        '&.Mui-selected': {
                          bgcolor: 'menu.item.selected',
                          '&:hover': { bgcolor: 'menu.item.selected' },
                        },
                        '&:hover': {
                          bgcolor: 'menu.item.hover',
                          color: theme.palette.menu.item.hoverText,
                        },
                      })}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 28,
                          color: 'primary.main',
                          alignSelf: 'flex-start',
                          mt: 0.25,
                        }}
                      >
                        <Icon size={16} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600} color="text.primary">
                            {cmd.command}
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.light"
                              fontWeight={400}
                            >
                              {' '}
                              &mdash; {cmd.label}
                            </Typography>
                          </Typography>
                        }
                        secondary={cmd.description}
                        secondaryTypographyProps={{ variant: 'caption2', color: 'text.light' }}
                      />
                    </ListItemButton>
                  );
                })}
              </List> */}
            </Paper>
          </Popper>

          <TextInput
            containerProps={{ ref: inputRef }}
            inputRef={inputElementRef}
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder={
              pastedImages.length > 0 ? 'Add a message about the image…' : 'Type a message…'
            }
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            inputProps={{ 'data-testid': 'agent-chat-input' }}
          />
          {/* Show Stop button when streaming, plus Send/Queue button always */}
          <Button
            variant="primary"
            size="small"
            onClick={() => void handleSend()}
            disabled={!input.trim()}
            data-testid="agent-chat-send-btn"
            sx={{ width: 'fit-content' }}
          >
            {streaming ? 'Queue' : 'Send'}
          </Button>
          {streaming && (
            <IconButton
              Icon={StopIcon}
              variant="error"
              onClick={() => void stopStreaming()}
              data-testid="agent-chat-stop-btn"
            />
          )}
        </Group>
      </Stack>
    </Stack>
  );
};
